// Helpers de Home: usuario, avatar, signos, países y notificaciones del feed.
// Extraído de HomeScreen.js para mantenerlo más liviano.
import { REST_API_ORIGIN } from '../platform/endpoints';

const API_ORIGIN = REST_API_ORIGIN;
const GAMI_ENABLED = String(process.env.REACT_APP_GAMIFICATION || '').toLowerCase() === '1';
const HOROSCOPE_XP_ENABLED = String(process.env.REACT_APP_HOROSCOPE_XP || '') === '1';

// Configuración de trivia

const TIME_PER_QUESTION_MS = 10000;    // 10 segundos por pregunta

// --- Resolve a stable user id from AuthContext user ---
function resolveUserId(user) {
  const v = user && (user._id || user.id || user.userId || user.uid);
  return v ? String(v) : '';
}

function resolveUserIdSafe(userMaybe) {
  const raw =
    userMaybe?._id ||
    userMaybe?.userId ||
    userMaybe?.id ||
    userMaybe?.uid ||
    userMaybe?.user?._id ||
    userMaybe?.user?.userId ||
    userMaybe?.user?.id;

  const clean = (v) => (v ? String(v).replace(/-DEVLOCAL$/i, '') : '');
  if (raw) return clean(raw);

  try {
    const tryKeys = ['me', 'user', 'auth', 'profile'];
    for (const k of tryKeys) {
      const val = localStorage.getItem(k);
      if (!val) continue;
      let obj = null;
      try { obj = JSON.parse(val); } catch { obj = null; }
      const ls =
        obj?._id ||
        obj?.userId ||
        obj?.id ||
        obj?.uid ||
        obj?.user?._id ||
        obj?.user?.userId ||
        obj?.user?.id;
      const out = clean(ls);
      if (out) return out;
    }
  } catch {}

  return '';
}

function resolveAvatar(u) {
  const fallback = '/profile_photos/default.png';

  // Prefer Cloudinary public_id when available so we can force correct orientation
  // and consistent square thumbnails for tiny notification previews.
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dbkclmafx';
  const photoPublicId = typeof u?.photoPublicId === 'string' ? u.photoPublicId.trim() : '';
  if (photoPublicId) {
    return `https://res.cloudinary.com/${cloudName}/image/upload/a_auto,q_auto,f_auto/${photoPublicId}.jpg`;
  }

  // Gather possible fields in priority order
  const candidates = [
    u?.avatarUrl,
    u?.photo,
    u?.profilePhoto,
    u?.avatar,
    u?.profilePhotoUrl,
    u?.photoPath,
  ];

  const raw = candidates.find(v => typeof v === 'string' && v.trim().length);
  if (!raw) return fallback;

  const s = String(raw).trim();

  // Allow safe absolute URLs (http/https), data URLs for images, and blob URLs
  if (/^(https?:\/\/|data:image\/|blob:)/i.test(s)) {
    // Cloudinary: force EXIF-aware orientation so previews don't appear rotated
    if (/^https?:\/\/res\.cloudinary\.com\//i.test(s) && s.includes('/upload/')) {
      return s.replace('/upload/', '/upload/a_auto,q_auto,f_auto/');
    }
    return s;
  }

  // Reject any other protocols for safety (e.g., javascript:)
  if (/^[a-zA-Z]+:\/\//.test(s)) {
    return fallback;
  }

  // Normalize relative paths (avoid leading slashes duplication)
  const clean = s.replace(/^\/+/, '');
  let url = `${API_ORIGIN}/${clean}`;

  // Optional cache‑buster if we have any version-ish field
  const v = (u && (u.photoVersion || u.avatarVersion || u.photoUpdatedAt || u.updatedAt))
    ? String(u.photoVersion || u.avatarVersion || u.photoUpdatedAt || u.updatedAt)
    : '';
  if (v) {
    url += (url.includes('?') ? '&' : '?') + `v=${encodeURIComponent(v)}`;
  }

  return url || fallback;
}

// Mapa de signos -> emoji
const SIGN_EMOJI = {
  aries: '♈',
  tauro: '♉',
  geminis: '♊',
  cancer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  escorpio: '♏',
  sagitario: '♐',
  capricornio: '♑',
  acuario: '♒',
  piscis: '♓',
};

function normalizeSignKey(str) {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')                // separa tilde: "cáncer" -> "cáncer"
    .replace(/[\u0300-\u036f]/g, ''); // borra tildes: "cáncer" -> "cancer"
}

// === Country helpers (flag + normalization) ===
function normalizeISO2Country(code) {
  if (!code || typeof code !== 'string') return '';
  return code.trim().slice(0, 2).toUpperCase();
}
function countryFlagEmoji(iso2) {
  const cc = normalizeISO2Country(iso2);
  if (!cc || cc.length !== 2) return '🌎';
  const A = 0x41; // 'A'
  const REGIONAL_INDICATOR = 0x1F1E6;
  const first = cc.charCodeAt(0) - A + REGIONAL_INDICATOR;
  const second = cc.charCodeAt(1) - A + REGIONAL_INDICATOR;
  try { return String.fromCodePoint(first) + String.fromCodePoint(second); } catch { return '🏳️'; }
}

// Country name helper (maps ISO2 -> human name)
function countryName(code, locale = 'es') {
  const names = {
    AR: 'Argentina',
    UY: 'Uruguay',
    CL: 'Chile',
    BR: 'Brasil',
    PE: 'Perú',
    PY: 'Paraguay',
    BO: 'Bolivia',
    CO: 'Colombia',
    VE: 'Venezuela',
    MX: 'México',
    ES: 'España',
    US: 'Estados Unidos',
    FR: 'Francia',
    IT: 'Italia',
    DE: 'Alemania',
    GB: 'Reino Unido',
  };
  const cc = normalizeISO2Country(code);
  if (!cc) return String(locale || '').toLowerCase().startsWith('en') ? 'Country' : 'País';
  try {
    if (typeof Intl.DisplayNames === 'function') {
      return new Intl.DisplayNames([locale || 'es'], { type: 'region' }).of(cc) || names[cc] || cc;
    }
  } catch {}
  return names[cc] || cc;
}

function formatRelativeTime(value, translate) {
  const ts = value ? new Date(value).getTime() : 0;
  if (!ts) return '';

  const diff = Date.now() - ts;
  if (!Number.isFinite(diff) || diff < 0) return '';

  const min = Math.floor(diff / 60000);
  const text = (key, options, fallback) => (
    typeof translate === 'function' ? translate(key, options) : fallback
  );
  if (min < 1) return text('notifications.now', {}, 'ahora');
  if (min < 60) return text('notifications.minutesAgo', { count: min }, `hace ${min} min`);

  const hr = Math.floor(min / 60);
  if (hr < 24) return text('notifications.hoursAgo', { count: hr }, `hace ${hr} h`);

  const day = Math.floor(hr / 24);
  if (day === 1) return text('notifications.yesterday', {}, 'ayer');
  return text('notifications.daysAgo', { count: day }, `hace ${day} días`);
}

function _extractNotifTextSnippet(value, max = 72) {
  const s = String(value || '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1)).trim()}…`;
}

function _feedNotifFamily(type) {
  const t = String(type || '').trim();
  if (t === 'feed_post_like' || t === 'feed_comment_like') return 'like';
  if (t === 'feed_comment') return 'comment';
  if (t === 'feed_reply') return 'reply';
  return 'activity';
}

function _feedNotifTargetSnippet(item) {
  const threadText =
    item?.comment?.text ||
    item?.commentText ||
    item?.replyToComment?.text ||
    item?.parentComment?.text ||
    '';

  const postText =
    item?.post?.text ||
    item?.postText ||
    item?.entity?.postText ||
    '';

  return _extractNotifTextSnippet(threadText || postText || item?.text || '');
}

function _groupFeedNotifItems(items, currentUserId = '', translate) {
  const list = Array.isArray(items) ? items : [];

  return list
    .filter((raw) => raw && typeof raw === 'object')
    .map((raw) => {
      const family = _feedNotifFamily(raw?.type);

      const actorName = String(
        raw?.actor?.fullName ||
        raw?.actor?.nickname ||
        raw?.actor?.username ||
        ''
      ).trim() || (typeof translate === 'function' ? translate('notifications.someone') : 'Alguien');

      const actorId = String(raw?.actor?._id || raw?.actor?.id || actorName).trim();
      const normalizedActorId = actorId.replace(/-DEVLOCAL$/i, '');
      const normalizedCurrentUserId = String(currentUserId || '').trim().replace(/-DEVLOCAL$/i, '');
      if (normalizedCurrentUserId && normalizedActorId && normalizedActorId === normalizedCurrentUserId) {
        return null;
      }

      const createdAt = raw?.createdAt ? new Date(raw.createdAt).getTime() : 0;
      const avatarUrl = raw?.actor ? resolveAvatar(raw.actor) : '';
      const targetSnippet = _feedNotifTargetSnippet(raw);

      let familyLabel = typeof translate === 'function' ? translate('notifications.activity') : 'Actividad';
      let familyIcon = '🪐';
      let accent = '#FFD700';
      let actionText = typeof translate === 'function' ? translate('notifications.interacted') : 'interactuó';
      let fallbackSubtitle = typeof translate === 'function' ? translate('notifications.inYourThread') : 'En tu hilo';

      if (family === 'reply') {
        familyLabel = typeof translate === 'function' ? translate('notifications.replies') : 'Respuestas';
        familyIcon = '↩️';
        accent = '#C9A7FF';
        actionText = typeof translate === 'function' ? translate('notifications.replied') : 'respondió';
        fallbackSubtitle = typeof translate === 'function' ? translate('notifications.inYourThread') : 'En tu hilo';
      } else if (family === 'comment') {
        familyLabel = typeof translate === 'function' ? translate('notifications.comments') : 'Comentarios';
        familyIcon = '💬';
        accent = '#8EE3FF';
        actionText = typeof translate === 'function' ? translate('notifications.commented') : 'comentó';
        fallbackSubtitle = typeof translate === 'function' ? translate('notifications.inYourThread') : 'En tu hilo';
      } else if (family === 'like') {
        familyLabel = typeof translate === 'function' ? translate('notifications.likes') : 'Me gusta';
        familyIcon = '👍';
        accent = '#FFD700';
        actionText = typeof translate === 'function' ? translate('notifications.liked') : 'le dio Me gusta';
        fallbackSubtitle = typeof translate === 'function' ? translate('notifications.inYourThread') : 'En tu hilo';
      }

            const cleanSnippet = String(targetSnippet || '').trim();
      const sentence = `${actorName} ${actionText}`;
      const subtitle = cleanSnippet
        ? `${sentence} · ${cleanSnippet}`
        : `${sentence} · ${fallbackSubtitle}`;

      return {
        key: String(raw?._id || raw?.id || `${family}:${actorId}:${createdAt || Date.now()}`),
        family,
        familyLabel,
        familyIcon,
        accent,
        count: 1,
        totalInteractions: 1,
        uniqueActorCount: 1,
        actorText: actorName,
        actorLead: actorName,
        avatarUrl,
        title: '',
        subtitle,
        meta: actionText,
        createdAt,
        time: formatRelativeTime(createdAt, translate),
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
}

// Normaliza y obtiene el emoji del signo
function getSignEmoji(sign) {
  if (!sign) return '';
  const key = normalizeSignKey(sign);
  return SIGN_EMOJI[key] || '';
}

export {
  API_ORIGIN,
  GAMI_ENABLED,
  HOROSCOPE_XP_ENABLED,
  TIME_PER_QUESTION_MS,
  resolveUserId,
  resolveUserIdSafe,
  resolveAvatar,
  SIGN_EMOJI,
  normalizeSignKey,
  normalizeISO2Country,
  countryFlagEmoji,
  countryName,
  formatRelativeTime,
  getSignEmoji,
  _groupFeedNotifItems,
};
